import { useMemo } from "react";
import { useAppSelector } from "../../app";
import { getAvatarUrl } from "../../user/utils/utils";

/**
 * Hook that manages avatar URLs for unique assignees in the document set overview.
 * Extracts unique assignees from the current document sets and provides
 * a cached mapping of user IDs to avatar URLs.
 */
const useAssigneeAvatars = () => {
  const documentSets = useAppSelector(
    (state) => state.documentSetsReducer.documentSets,
  );

  // Extract unique assignees and create a map of userId -> avatarUrl
  const assigneeAvatarMap = useMemo(() => {
    const avatarMap = new Map<string, string>();

    documentSets.forEach((docSet) => {
      if (docSet.assignee?.id && !avatarMap.has(docSet.assignee.id)) {
        avatarMap.set(docSet.assignee.id, getAvatarUrl(docSet.assignee.id));
      }
    });

    return avatarMap;
  }, [documentSets]);

  /**
   * Get the avatar URL for a given user ID.
   * Returns the cached URL if available, otherwise generates it on demand.
   */
  const getAssigneeAvatarUrl = (userId: string | undefined): string | null => {
    if (!userId) return null;

    // Return from cache if available
    if (assigneeAvatarMap.has(userId)) {
      return assigneeAvatarMap.get(userId)!;
    }

    // Generate URL on demand for assignees not in the current document set
    return getAvatarUrl(userId);
  };

  return {
    assigneeAvatarMap,
    getAssigneeAvatarUrl,
  };
};

export default useAssigneeAvatars;
